import {
  Component,
  signal,
  WritableSignal,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from '@openng/optimus-ui/dialog';
import {
  RadioButtonClickEvent,
  RadioButtonModule,
} from '@openng/optimus-ui/radiobutton';
import { firstValueFrom } from 'rxjs';

import { ControlRoomEventReviewEntry } from 'src/app/model/monlogs/control-room-event-review-entry';
import { MonlogsApiService } from 'src/app/services/api/monlogs-api.service';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { NoteEditorComponent } from 'src/app/core/components/note-editor.component';
import { ButtonModule } from '@openng/optimus-ui/button';

@Component({
  selector: 'app-event-review-dialog',
  imports: [
    DialogModule,
    ProgressSpinnerComponent,
    TranslateModule,
    NoteEditorComponent,
    ReactiveFormsModule,
    RadioButtonModule,
    ButtonModule,
  ],
  templateUrl: './event-review-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './event-review-dialog.component.scss',
})
export class EventReviewDialogComponent {
  private _fb = inject(FormBuilder);
  private _translateService = inject(TranslateService);
  private _monLogApi = inject(MonlogsApiService);

  readonly monLogId = input<string>();
  readonly entryChangedEvent = output();

  currentlyEditingEntry: ControlRoomEventReviewEntry | null = null;
  form: FormGroup;
  showEntryDialog: boolean = false;
  editorReadOnly: WritableSignal<boolean> = signal(false);
  editorWorking: WritableSignal<boolean> = signal(false);

  constructor() {
    this.form = this._fb.group({
      note: [null, Validators.required],
      controlRoomInvolved: [false],
    });
  }

  cancelEntryForm(): void {
    this.showEntryDialog = false;
    this.currentlyEditingEntry = null;
  }

  validateForm() {
    this.form.updateValueAndValidity();
  }

  processEntry() {
    this.editorWorking.set(true);
    if (this.currentlyEditingEntry) {
      // Edit mode
      this._monLogApi
        .updateCREREntry(this.currentlyEditingEntry.id, {
          note: this.form.get('note')?.value,
          controlRoomInvolved: this.form.get('controlRoomInvolved')?.value,
        })
        .subscribe({
          next: (editedEntry) => {
            this.entryChangedEvent.emit();
            this.showEntryDialog = false;
            this.editorWorking.set(false);
          },
          error: (error) => {
            console.error(error);
            this.editorWorking.set(false);
          },
        });
    } else {
      // Create mode
      this._monLogApi
        .createCREREntry(this.monLogId()!, {
          note: this.form.get('note')?.value,
          controlRoomInvolved: this.form.get('controlRoomInvolved')?.value,
        })
        .subscribe({
          next: (newEntry) => {
            this.entryChangedEvent.emit();
            this.showEntryDialog = false;
            this.editorWorking.set(false);
          },
          error: (error) => {
            console.error(error);
            // show alert message
            this.editorWorking.set(false);
          },
        });
    }
  }

  async beginEditting(entry: ControlRoomEventReviewEntry | null) {
    this.showEntryDialog = true;

    setTimeout(async () => {
      if (!entry) {
        // No entry passed in, setup new form defaults
        const defaultText = await firstValueFrom(
          this._translateService.get('MONLOG.DEFAULT_TEXT.CRER_NOT_INVOLVED')
        );
        this.form.patchValue({
          note: defaultText,
          controlRoomInvolved: false,
        });
        this.setNoteEnabled(false);
        this.form.updateValueAndValidity();
      } else {
        // Setup editting
        this.currentlyEditingEntry = entry;
        this.form.patchValue({
          note: entry.note,
          controlRoomInvolved: entry.controlRoomInvolved,
        });
        this.setNoteEnabled(entry.controlRoomInvolved);
      }
    }, 50);
  }

  setNoteEnabled(enabled: boolean): void {
    this.editorReadOnly.set(!enabled);
  }

  async crInvolvedChanged($event: RadioButtonClickEvent) {
    const value: boolean = $event.value;
    if (value) {
      this.form.patchValue({
        note: '',
      });
      this.setNoteEnabled(true);
    } else {
      const defaultText = await firstValueFrom(
        this._translateService.get('MONLOG.DEFAULT_TEXT.CRER_NOT_INVOLVED')
      );
      this.form.patchValue({
        note: defaultText,
      });
      this.setNoteEnabled(false);
    }
    this.form.updateValueAndValidity();
  }
}
